import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, getStationSession } from '../api';
import type { Race, Station, TimingPoint } from '../types';

export interface StationContext {
  station: Station;
  race: Race;
  timingPoint: TimingPoint;
}

export function useStationContext() {
  const session = getStationSession();
  return useQuery({
    queryKey: ['station-me', session?.stationId],
    queryFn: () => api<StationContext>('/station/me', { auth: 'station' }),
    enabled: !!session,
    retry: false,
  });
}

export async function openCamera(fps: number): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      frameRate: { ideal: fps, max: fps },
    },
  });
}

export type WakeState = 'on' | 'off' | 'unsupported' | 'refused';

/**
 * "Recipe mode": keep the screen on while a page is open. Browsers drop the
 * lock whenever the page is hidden, so it's taken again each time the page
 * comes back. Phones that can record for Splitline (iOS 16.4+, current
 * Chrome) all support it; when it's refused (e.g. Low Power Mode), the state
 * says so and the volunteer sets Auto-Lock to Never instead.
 */
export class ScreenWake {
  private lock: WakeLockSentinel | null = null;
  private active = false;
  state: WakeState = 'off';
  private listeners = new Set<(s: WakeState) => void>();
  private readonly onVisible = () => {
    if (document.visibilityState === 'visible' && this.active) void this.acquire();
  };
  // Some browsers only grant the lock after the person touches the page: try again on every tap until it holds.
  private readonly onTap = () => {
    if (this.active && this.state !== 'on') void this.acquire();
  };

  onChange(fn: (s: WakeState) => void) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private set(state: WakeState) {
    this.state = state;
    this.listeners.forEach((fn) => fn(state));
  }

  async start() {
    this.active = true;
    document.addEventListener('visibilitychange', this.onVisible);
    document.addEventListener('pointerdown', this.onTap, { passive: true });
    await this.acquire();
  }

  private async acquire() {
    if (!('wakeLock' in navigator)) return this.set('unsupported');
    if (this.lock && !this.lock.released) return;
    try {
      this.lock = await navigator.wakeLock.request('screen');
      this.lock.addEventListener('release', () => {
        // Released by the browser (page hidden): taken again when it's visible.
        if (this.active) this.set('off');
      });
      this.set('on');
    } catch {
      this.lock = null;
      this.set('refused');
    }
  }

  async stop() {
    this.active = false;
    document.removeEventListener('visibilitychange', this.onVisible);
    document.removeEventListener('pointerdown', this.onTap);
    await this.lock?.release().catch(() => undefined);
    this.lock = null;
    this.set('off');
  }
}

/** Hold the screen on while `enabled`; returns the lock's state for display. */
export function useScreenWake(enabled: boolean): WakeState {
  const [state, setState] = useState<WakeState>('off');
  useEffect(() => {
    if (!enabled) return;
    const wake = new ScreenWake();
    const off = wake.onChange(setState);
    void wake.start();
    return () => {
      off();
      void wake.stop();
    };
  }, [enabled]);
  return state;
}
